/**
 * Copyright © 2026 Adnuntius AS.
 */
import {AdnListHelperService} from '../common/controller/list-helper.service';
import {ChangeDetectorRef, Component, Inject, NgZone, OnChanges} from '@angular/core';
import * as _ from 'lodash';
import template from './external-ad-unit-edit-page.component.html';
import {AppEventBusService} from '../common/events/app-event-bus.service';

import {AdminResourceEditPageBase} from '../common/controller/admin-resource-edit-page-base';
import {AdUnitResource} from '../../components/api/resources/resource/adunit.resource';
import {CreativeResource} from '../../components/api/resources/resource/creative.resource';
import {ExternalDemandSourceResource} from '../../components/api/resources/resource/external-demand-source.resource';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {deferMarkForCheck} from '../common/util/defer-mark-for-check';

@Component({
  standalone: false,
  selector: 'adn-external-ad-unit-edit-page',
  template
})
export class ExternalAdUnitEditPageComponent extends AdminResourceEditPageBase implements OnChanges {
  readonly externalAdTypes = ['HTML', 'TEXT', 'VIDEO', 'FLASH', 'IMAGE', 'MIXED'];

  extDemandSources: any[] | false = false;

  readonly usageResource: any = {
    creativeList: {}
  };

  private listInitialized = false;

  constructor(
    eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) UserProfileSaver: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(ExternalDemandSourceResource) private ExternalDemandSource: any,
    @Inject(AdUnitResource) private AdUnit: any,
    @Inject(CreativeResource) private Creative: any,
    private adnListHelper: AdnListHelperService,
    private cdr: ChangeDetectorRef,
    ngZone: NgZone
  ) {
    super(eventBus, UserProfileSaver, LocalNetworkProfile, ngZone);
    this.deferFormDirtyTracking();

    this.ExternalDemandSource.query().$promise.then((page: any) => {
      deferMarkForCheck(this.cdr, () => {
        this.extDemandSources = page.results || [];
        this.applyDefaultDemandSource();
        this.resolveDemandSourceReference();
        this.resumeFormDirtyTracking();
      });
    });
  }

  ngOnChanges(): void {
    super.ngOnChanges();
    if (this.model?.id && !this.listInitialized) {
      this.listInitialized = true;
      const moreParams = {externalAdUnit: this.model.id};
      this.adnListHelper.setUpBasicList(this.usageResource, this.AdUnit, 'fullAdUnitsForExternalAdUnit', {
        moreParams,
        noInitQuery: true
      });
      this.adnListHelper.setUpBasicList(this.usageResource.creativeList, this.Creative, 'fullCreativesForExternalAdUnit', {
        moreParams,
        noInitQuery: true
      });
      if (!this.isNew) {
        this.usageResource.makeQuery?.();
        this.usageResource.creativeList.makeQuery?.();
      }
    }
  }

  protected afterInitEditingModel(): void {
    this.editingModel.dimensions = _.isArray(this.editingModel.dimensions) ? this.editingModel.dimensions : [[]];
    this.editingModel.dimensions[0] = _.isArray(this.editingModel.dimensions[0]) ? this.editingModel.dimensions[0] : [];
    this.applyDefaultDemandSource();
    this.resolveDemandSourceReference();
  }

  protected afterInitAfterSave(): void {
    if (!this.isNew) {
      this.usageResource.makeQuery?.();
      this.usageResource.creativeList.makeQuery?.();
    }
    this.resolveDemandSourceReference();
  }

  protected onBeforeSubmit(): void {
    // Required for API backwards compatibility with singular width/height fields.
    delete this.editingModel.width;
    delete this.editingModel.height;
  }

  addDimensions(): void {
    this.editingModel.dimensions = this.editingModel.dimensions || [];
    this.editingModel.dimensions.push([]);
    this.markExternalFormDirty();
  }

  removeDimensions(index: number): void {
    _.pullAt(this.editingModel.dimensions, index);
    this.markExternalFormDirty();
  }

  onDemandSourceChange(): void {
    this.resolveDemandSourceReference();
    this.markExternalFormDirty();
  }

  private applyDefaultDemandSource(): void {
    if (!_.isArray(this.extDemandSources) || this.extDemandSources.length !== 1) {
      return;
    }
    if (!this.editingModel.externalDemandSource) {
      this.editingModel.externalDemandSource = this.extDemandSources[0];
    }
  }

  private resolveDemandSourceReference(): void {
    if (!_.isArray(this.extDemandSources) || !this.editingModel?.externalDemandSource) {
      return;
    }
    const extDemSourceId = _.get(this.editingModel, ['externalDemandSource', 'id'])
      || (_.isString(this.editingModel.externalDemandSource) ? this.editingModel.externalDemandSource : null);
    if (!extDemSourceId) {
      return;
    }
    const fullExtDemandSource = _.find(this.extDemandSources, ['id', extDemSourceId]);
    if (fullExtDemandSource) {
      this.editingModel.externalDemandSource = fullExtDemandSource;
    }
  }
}
